











































































import {
  defineComponent,
  PropType,
  ref,
  computed,
  ComputedRef,
} from '@vue/composition-api';
import {
  EpigraphicWord,
  EpigraphicSign,
  EpigraphicUnitType,
  MarkupType,
  EditorDiscourseWord,
  ReorderSignPayload,
  SearchSpellingResultRow,
} from '@oare/types';
import ConnectDiscourseItem from '@/views/Texts/CollectionTexts/AddTexts/Discourse/components/ConnectDiscourseItem.vue';
import sl from '@/serviceLocator';

export default defineComponent({
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    word: {
      type: Object as PropType<EpigraphicWord>,
      required: true,
    },
    textUuid: {
      type: String,
      required: true,
    },
  },
  components: {
    ConnectDiscourseItem,
  },
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const reorderSignsLoading = ref(false);

    const stepOneComplete = computed(() => {
      return selectedSigns.value.length === 2;
    });

    const formsLoaded = ref(false);

    const reorderSigns = async () => {
      try {
        reorderSignsLoading.value = true;

        if (selectedSigns.value.length !== 2) {
          throw new Error('Must select two signs to reorder');
        }

        const newSpelling = getUpdatedSignsWithSeparators();

        const payload: ReorderSignPayload = {
          type: 'reorderSign',
          textUuid: props.textUuid,
          spelling: newSpelling,
          spellingUuid: form.value ? form.value.spellingUuid : null,
          transcription: form.value ? form.value.form.form : null,
          signUuids: selectedSigns.value.map(s => s.uuid),
          discourseUuid: props.word.discourseUuid,
        };

        await server.editText(payload);
        emit('reset-renderer');
      } catch (err) {
        actions.showErrorSnackbar('Error reordering signs', err as Error);
      } finally {
        emit('input', false);
        emit('reset-current-edit-action');
        reorderSignsLoading.value = false;
      }
    };

    const form = ref<SearchSpellingResultRow>();
    const step = ref(1);

    const goBack = () => {
      step.value = 1;
      form.value = undefined;
    };

    const selectedSigns = ref<EpigraphicSign[]>([]);

    const signCanBeSelected = (sign: EpigraphicSign) => {
      if (selectedSigns.value.map(s => s.uuid).includes(sign.uuid)) {
        return true;
      }

      if (selectedSigns.value.length === 0) {
        return true;
      }

      if (selectedSigns.value.length >= 2) {
        return false;
      }

      if (selectedSigns.value.length === 1) {
        const firstObjOnTablet = selectedSigns.value[0].objOnTablet;
        if (
          sign.objOnTablet === firstObjOnTablet - 1 ||
          sign.objOnTablet === firstObjOnTablet + 1
        ) {
          return true;
        }
      }

      return false;
    };

    const getUpdatedSignsWithSeparators = () => {
      const pieces: {
        uuid: string;
        reading: string;
        type: EpigraphicUnitType | null;
        markup: MarkupType[];
      }[] = props.word.signs.map(sign => ({
        uuid: sign.uuid,
        reading: sign.reading || '',
        type: sign.type,
        markup: sign.markups.map(unit => unit.type),
      }));

      const indexOfFirst = pieces
        .map(p => p.uuid)
        .indexOf(selectedSigns.value[0].uuid);
      const indexOfSecond = pieces
        .map(p => p.uuid)
        .indexOf(selectedSigns.value[1].uuid);

      const newPieces = [
        ...pieces.slice(0, indexOfFirst),
        pieces[indexOfSecond],
        pieces[indexOfFirst],
        ...pieces.slice(indexOfSecond + 1),
      ];

      const newWord = newPieces.map((sign, index) => {
        const nextSign =
          index !== newPieces.length - 1 ? newPieces[index + 1] : null;

        let newSeparator = '';
        if (nextSign) {
          if (
            !sign.markup.includes('phoneticComplement') &&
            nextSign.markup.includes('phoneticComplement')
          ) {
            newSeparator = '';
          } else if (
            sign.type === 'determinative' ||
            nextSign.type === 'determinative'
          ) {
            newSeparator = '';
          } else if (
            sign.type === 'phonogram' ||
            nextSign.type === 'phonogram'
          ) {
            newSeparator = '-';
          } else if (sign.type === 'number' && nextSign.type === 'number') {
            newSeparator = '+';
          } else if (sign.type === 'logogram' || nextSign.type === 'logogram') {
            newSeparator = '.';
          }
        }

        let newReading = sign.reading;
        if (sign.type === 'determinative') {
          newReading = `(${newReading})`;
        }

        return {
          ...sign,
          reading: newReading,
          separator: newSeparator,
        };
      });

      let newWordReading = '';
      newWord.forEach(sign => {
        newWordReading += sign.reading;
        if (sign.separator) {
          newWordReading += sign.separator;
        }
      });
      return newWordReading
        .replace(/([[\]{}⸢⸣«»‹›:;*?\\!])|(".+")|('.+')|(^\/)+/g, '')
        .replace(/<[^>]*>/g, '');
    };

    const editorDiscourseWord: ComputedRef<EditorDiscourseWord> = computed(
      () => {
        const newWord = getUpdatedSignsWithSeparators();
        return {
          discourseUuid: props.word.discourseUuid,
          spelling: newWord,
          type: 'word',
        };
      }
    );

    return {
      reorderSignsLoading,
      stepOneComplete,
      formsLoaded,
      reorderSigns,
      step,
      form,
      goBack,
      selectedSigns,
      signCanBeSelected,
      getUpdatedSignsWithSeparators,
      editorDiscourseWord,
    };
  },
});
